import type {
  AdminContractor,
  AdminContractorSummary,
  CheckoutErrorResponse,
  ContractorPricing,
  ContractorPricingUpdate,
  ContractorOrder,
  ContractorOrderCreated,
  ContractorOrderRequest,
  ContractorQuote,
  DeliveryAddress,
  CheckoutSessionRequest,
  CheckoutSessionResponse,
  FacetsResponse,
  Me,
  ProfileUpdate,
  InventoryResponse,
  Order,
  StaffOrder,
  StaffOrderAction,
  Product,
  ProductListResponse,
  ProductQuery,
  SearchSuggestion,
} from "@neus/shared";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      accept: "application/json",
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  const body = res.status === 204 ? undefined : await res.json().catch(() => undefined);
  if (!res.ok) {
    const message = (body as { error?: string } | undefined)?.error ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "" && v !== false) s.set(k, String(v));
  const str = s.toString();
  return str ? `?${str}` : "";
}

export const api = {
  version: () => request<{ version?: string }>("/api/health"),
  products: (q: ProductQuery) => request<ProductListResponse>(`/api/products${qs({ ...q })}`),
  product: (sku: string) => request<Product>(`/api/products/${encodeURIComponent(sku)}`),
  /** Clears the session; the browser then goes to `signOutUrl` to end the Entra session too. */
  logout: (everywhere = false) =>
    request<{ signOutUrl: string }>("/api/auth/logout", { method: "POST", body: JSON.stringify({ everywhere }) }),
  /** The signed-in user, or null when signed out (or when accounts are off). */
  me: async (): Promise<Me | null> => {
    try {
      return await request<Me>("/api/me");
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 404)) return null;
      throw err;
    }
  },
  /** With a delivery address, the quote adds the delivery fee and taxes at the job site. */
  contractorQuote: (lines: { sku: string; qty: number }[], deliveryAddress?: DeliveryAddress) =>
    request<ContractorQuote>("/api/contractor/quote", {
      method: "POST",
      body: JSON.stringify({ lines, ...(deliveryAddress && { delivery: { address: deliveryAddress } }) }),
    }),
  /** Store orders placed while signed in, newest first. */
  myOrders: () => request<Order[]>("/api/me/orders"),
  /** Send the cart to the store for pickup, to pay there (409 with `problems` when stock fell short). */
  createPickupOrder: (body: CheckoutSessionRequest) =>
    request<Order>("/api/me/pickup-orders", { method: "POST", body: JSON.stringify(body) }),
  /** Email a pay-in-store pickup order, with its QR code, to the account's address (pickupOrderEmails). */
  emailPickupOrder: (id: string) =>
    request<{ to: string }>(`/api/me/orders/${encodeURIComponent(id)}/email`, { method: "POST", body: "{}" }),
  contractorOrders: () => request<ContractorOrder[]>("/api/contractor/orders"),
  /** With the Checkout Session id after paying, so the deposit is recorded even before the webhook. */
  contractorOrder: (id: string, sessionId?: string) =>
    request<ContractorOrder>(`/api/contractor/orders/${encodeURIComponent(id)}${qs({ session_id: sessionId })}`),
  createContractorOrder: (order: ContractorOrderRequest) =>
    request<ContractorOrderCreated>("/api/contractor/orders", { method: "POST", body: JSON.stringify(order) }),
  /** A fresh deposit payment, for one that wasn't completed in time. */
  contractorDeposit: (id: string) =>
    request<ContractorOrderCreated>(`/api/contractor/orders/${encodeURIComponent(id)}/deposit`, {
      method: "POST",
      body: "{}",
    }),
  /** Staff: online and job orders to fulfill, or the ones already handed over. */
  staffOrders: (view: "active" | "completed") => request<StaffOrder[]>(`/api/staff/orders${qs({ view })}`),
  staffOrder: (kind: string, key: string) =>
    request<StaffOrder>(`/api/staff/orders/${encodeURIComponent(kind)}/${encodeURIComponent(key)}`),
  updateStaffOrder: (kind: string, key: string, action: StaffOrderAction) =>
    request<StaffOrder>(`/api/staff/orders/${encodeURIComponent(kind)}/${encodeURIComponent(key)}`, {
      method: "POST",
      body: JSON.stringify(action),
    }),
  /** Staff: contractor accounts, filtered by name, email or company. */
  adminContractors: (q = "") => request<AdminContractorSummary[]>(`/api/staff/contractors${qs({ q })}`),
  adminContractor: (id: string) => request<AdminContractor>(`/api/staff/contractors/${encodeURIComponent(id)}`),
  /** Staff: the pricing for all contractors (null when there's none). */
  allContractorPricing: () => request<ContractorPricing | null>("/api/staff/contractor-pricing"),
  updateAllContractorPricing: (pricing: ContractorPricingUpdate) =>
    request<ContractorPricing | null>("/api/staff/contractor-pricing", {
      method: "PUT",
      body: JSON.stringify(pricing),
    }),
  /** Staff, with contractor approval on: activate or deactivate a contractor account. */
  updateContractorStatus: (id: string, active: boolean) =>
    request<AdminContractor>(`/api/staff/contractors/${encodeURIComponent(id)}/status`, {
      method: "PUT",
      body: JSON.stringify({ active }),
    }),
  updateContractorPricing: (id: string, pricing: ContractorPricingUpdate) =>
    request<AdminContractor>(`/api/staff/contractors/${encodeURIComponent(id)}/pricing`, {
      method: "PUT",
      body: JSON.stringify(pricing),
    }),
  updateMe: (update: ProfileUpdate) => request<Me>("/api/me", { method: "PUT", body: JSON.stringify(update) }),
  suggest: (q: string, limit = 8) => request<SearchSuggestion[]>(`/api/search/suggest${qs({ q, limit })}`),
  facets: () => request<FacetsResponse>("/api/facets"),
  inventory: (since: number) => request<InventoryResponse>(`/api/inventory?since=${since}`),
  createCheckoutSession: (body: CheckoutSessionRequest) =>
    request<CheckoutSessionResponse>("/api/checkout/session", { method: "POST", body: JSON.stringify(body) }),
  orderBySession: async (sessionId: string) => {
    const res = await fetch(`/api/orders/by-session/${encodeURIComponent(sessionId)}`);
    const body = await res.json().catch(() => undefined);
    if (res.status === 202) return { status: "processing" as const };
    if (!res.ok) throw new ApiError(res.status, body?.error ?? "Order lookup failed", body);
    return { status: "paid" as const, order: body as Order };
  },
};

export function checkoutProblems(err: unknown): CheckoutErrorResponse["problems"] {
  return err instanceof ApiError && err.status === 409 ? (err.body as CheckoutErrorResponse).problems : undefined;
}
